// BAIQL Validator
// Fast line-by-line validation with simple parsing heuristics

const COMMANDS = new Set([
  'BATCH_START','END_BATCH','LOAD','PROMPT','RUN','OUTPUT','GET','SET','IF','END_IF','LOOP','END_LOOP','STEP','MAKE_LOG','WRITE_LOG','ERROR','MODEL','LIMIT','PATH_IN','PATH_OUT','GLOBAL','VALIDATE','CHECKPOINT',
  'ROUTE_ON','WHEN','DEFAULT','END_ROUTE','PARALLEL','SEQUENTIAL','TAG','TIMEOUT','DEADLINE','FALLBACK'
]);

const LOAD_TYPES = ['FILE','DIR','REF','WORK','LINES'];

export function debounce(fn, wait = 150) {
  let t = null; let lastArgs = null;
  return (...args) => {
    lastArgs = args;
    if (t) clearTimeout(t);
    t = setTimeout(() => { t = null; fn(...lastArgs); }, wait);
  };
}

export function validateBaiql(source) {
  const errors = [];
  const lines = (source || '').split(/\r?\n/);

  // Structure stacks
  const batchStack = [];
  const ifStack = [];
  const loopStack = [];
  const routeStack = [];
  let inTagBlock = false;

  // State within current batch
  let inBatch = false;
  let seenPromptInBatch = false;
  let seenRunInBatch = false;

  // Load tracking per batch
  let hasRef = false, hasFile = false, hasDir = false, hasWork = false;
  let fileCount = 0, dirCount = 0, workCount = 0; // allow many REF

  // Variables
  const definedVars = new Set();

  // Helpers
  const pushError = (lineIdx, message, severity = 'error') => {
    errors.push({ line: lineIdx + 1, message, severity });
  };

  const resetBatchState = () => {
    inBatch = false; seenPromptInBatch = false; seenRunInBatch = false;
    hasRef = false; hasFile = false; hasDir = false; hasWork = false;
    fileCount = 0; dirCount = 0; workCount = 0;
  };

  // Pre-pass: capture variable definitions (GET/SET left-hand names) and LINES_* defined column names
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.replace(/#.*/, '').trim();
    if (!line) continue;
    const mGet = line.match(/^\s*(GET|SET)\s+([A-Za-z_][A-Za-z0-9_]*)\b/i);
    if (mGet) {
      definedVars.add(mGet[2].toUpperCase());
      // fallthrough to also detect LINES_* special cases
    }
    // OCR flags: allow without warnings
    if (/^\s*SET\s+OCR_IF_EMPTY\b/i.test(line)) {
      definedVars.add('OCR_IF_EMPTY');
      continue;
    }
    const mOcrLangs = line.match(/^\s*SET\s+OCR_LANGS\s*=\s*(["'])(.*?)\1\s*$/i);
    if (mOcrLangs) {
      definedVars.add('OCR_LANGS');
      // Optionally record each language token as a pseudo-var (not needed runtime)
      continue;
    }
    // SET LINES_NOHEADER = "a,b,c" => define a, b, c
    const mNoHdr = line.match(/^\s*SET\s+LINES_NOHEADER\s*=\s*(["'])(.*?)\1\s*$/i);
    if (mNoHdr) {
      const names = (mNoHdr[2] || '')
        .split(/[;\,\t,]/)
        .map(s => s.trim())
        .filter(Boolean);
      for (const n of names) definedVars.add(n.toUpperCase());
      continue;
    }
    // SET LINES_HEADER = "a,b,c" (optional explicit names)
    const mHdr = line.match(/^\s*SET\s+LINES_HEADER\s*=\s*(["'])(.*?)\1\s*$/i);
    if (mHdr) {
      const names = (mHdr[2] || '')
        .split(/[;\,\t,]/)
        .map(s => s.trim())
        .filter(Boolean);
      for (const n of names) definedVars.add(n.toUpperCase());
      continue;
    }
  }

  // Main pass
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.replace(/#.*/, '').trim();
    if (!line) continue;

    // TIMEOUT seconds [SKIP|HALT|RETRY]
    if (/^TIMEOUT\b/i.test(line)) {
      const mT = line.match(/^TIMEOUT\s+(\d+)/i);
      if (!mT) pushError(i, 'TIMEOUT syntax: TIMEOUT seconds [SKIP|HALT|RETRY]');
      const actT = (line.match(/\b(SKIP|HALT|RETRY)\b/i) || [])[1];
      if (actT && !/^(SKIP|HALT|RETRY)$/i.test(actT)) pushError(i, 'TIMEOUT action must be SKIP|HALT|RETRY');
      continue;
    }

    // DEADLINE minutes [HALT|PARTIAL]
    if (/^DEADLINE\b/i.test(line)) {
      const mD = line.match(/^DEADLINE\s+(\d+)/i);
      if (!mD) pushError(i, 'DEADLINE syntax: DEADLINE minutes [HALT|PARTIAL]');
      const actD = (line.match(/\b(HALT|PARTIAL)\b/i) || [])[1];
      if (actD && !/^(HALT|PARTIAL)$/i.test(actD)) pushError(i, 'DEADLINE action must be HALT|PARTIAL');
      continue;
    }

    // FALLBACK model1 [: model2 [: model3]] (allow GLOBAL prefix)
    if (/^(?:GLOBAL\s+)?FALLBACK\b/i.test(line)) {
      const m = line.match(/^(?:GLOBAL\s+)?FALLBACK\s+(.+)$/i);
      if (!m) { pushError(i, 'FALLBACK syntax: FALLBACK model1 [: model2 [: model3]]'); continue; }
      const parts = m[1].split(':').map(s => s.trim().replace(/^["']|["']$/g, '')).filter(Boolean);
      if (parts.length < 1 || parts.length > 3) pushError(i, 'FALLBACK requires 1 to 3 models separated by :');
      continue;
    }

    // Unknown top-level command sanity check (optional warning)
    const firstTok = (line.match(/^[A-Z_]+/) || [''])[0];
    if (firstTok && !COMMANDS.has(firstTok)) {
      // Heuristic: warn on unknown leading tokens
      if (/^[A-Z_]{3,}$/.test(firstTok)) {
        pushError(i, `Unknown command: ${firstTok}`, 'warning');
      }
    }

    if (/^BATCH_START\b/i.test(line)) {
      batchStack.push(i);
      inBatch = true;
      seenPromptInBatch = false; seenRunInBatch = false;
      hasRef = false; hasFile = false; hasDir = false; hasWork = false;
      fileCount = 0; dirCount = 0; workCount = 0;
      continue;
    }
    if (/^END_BATCH\b/i.test(line)) {
      if (!inBatch || batchStack.length === 0) {
        pushError(i, 'END_BATCH without matching BATCH_START');
      } else {
        batchStack.pop();
      }
      resetBatchState();
      continue;
    }
    if (/^TAG\b/i.test(line)) {
      // TAG {key} = value or TAG key = value
      const ok = /^TAG\s+(\{[A-Za-z_][A-Za-z0-9_]*\}|[A-Za-z_][A-Za-z0-9_]*)\s*=/.test(line);
      if (!ok) pushError(i, 'TAG syntax: TAG {key}|key = value');
      inTagBlock = true;
      continue;
    }
    // Any non-TAG ends a TAG block
    if (inTagBlock) { inTagBlock = false; }
    if (/^PARALLEL\b/i.test(line)) {
      const m = line.match(/^PARALLEL\s+(\d+)/i);
      if (!m) pushError(i, 'PARALLEL requires a number 1..5');
      else {
        const n = parseInt(m[1], 10);
        if (isNaN(n) || n < 1 || n > 5) pushError(i, 'PARALLEL range is 1..5');
      }
      continue;
    }
    if (/^SEQUENTIAL\b/i.test(line)) { continue; }
    if (/^ROUTE_ON\b/i.test(line)) { routeStack.push(i); continue; }
    if (/^END_ROUTE\b/i.test(line)) { if (routeStack.length === 0) pushError(i, 'END_ROUTE without matching ROUTE_ON'); else routeStack.pop(); continue; }
    if (/^WHEN\b/i.test(line)) { if (routeStack.length === 0) pushError(i, 'WHEN outside of ROUTE_ON'); /* allow */ }
    if (/^DEFAULT\b/i.test(line)) { if (routeStack.length === 0) pushError(i, 'DEFAULT outside of ROUTE_ON'); /* allow */ }
    if (/^IF\b/i.test(line)) {
      ifStack.push(i);
      continue;
    }
    if (/^END_IF\b/i.test(line)) {
      if (ifStack.length === 0) pushError(i, 'END_IF without matching IF'); else ifStack.pop();
      continue;
    }
    if (/^LOOP\b/i.test(line)) { loopStack.push(i); continue; }
    if (/^END_LOOP\b/i.test(line)) { if (loopStack.length === 0) pushError(i, 'END_LOOP without matching LOOP'); else loopStack.pop(); continue; }

  // STEP: allow inline PROMPT to satisfy RUN-after-PROMPT rule
  if (/^STEP\b/i.test(line)) {
    if (/\bPROMPT\b/i.test(line)) {
      if (!inBatch) pushError(i, 'PROMPT outside of BATCH_START/END_BATCH');
      seenPromptInBatch = true;
    }
    // do not continue; allow other checks (e.g., var usage) to run
  }

    if (/^PROMPT\b/i.test(line)) {
      if (!inBatch) pushError(i, 'PROMPT outside of BATCH_START/END_BATCH');
      seenPromptInBatch = true;
    }
    if (/^RUN\b/i.test(line)) {
      if (!inBatch) pushError(i, 'RUN outside of BATCH_START/END_BATCH');
      if (!seenPromptInBatch) pushError(i, 'RUN must appear after PROMPT');
      seenRunInBatch = true;
    }

    if (/^LOAD\b/i.test(line)) {
      if (!inBatch) pushError(i, 'LOAD outside of BATCH_START/END_BATCH');

      // Syntax per spec: LOAD <name> AS <TYPE> [CACHE|NOCACHE]
      // Extract tokens
      let hasAs = /\bAS\b/i.test(line);
      let hasFileTok = /\bFILE\b/i.test(line);
      let hasDirTok = /\bDIR\b/i.test(line);
      let hasRefTok = /\bREF\b/i.test(line);
      let hasWorkTok = /\bWORK\b/i.test(line);
      let hasLinesTok = /\bLINES\b/i.test(line);

      // Allow 2-line syntax: next non-empty line can be "AS <TYPE>"
      if (!hasAs || !(hasFileTok || hasDirTok || hasRefTok || hasWorkTok || hasLinesTok)) {
        // find next non-empty, non-comment line
        let j = i + 1;
        while (j < lines.length) {
          const rawNext = lines[j];
          const next = (rawNext || '').replace(/#.*/, '').trim();
          if (!next) { j++; continue; }
          const mAs = next.match(/^AS\s+(FILE|DIR|REF|WORK|LINES)\b/i);
          if (mAs) {
            hasAs = true;
            const t = mAs[1].toUpperCase();
            hasFileTok = hasFileTok || t === 'FILE';
            hasDirTok = hasDirTok || t === 'DIR';
            hasRefTok = hasRefTok || t === 'REF';
            hasWorkTok = hasWorkTok || t === 'WORK';
            hasLinesTok = hasLinesTok || t === 'LINES';
          }
          break;
        }
      }

      // Track counts/flags
      if (hasFileTok) { hasFile = true; fileCount++; }
      if (hasDirTok)  { hasDir  = true; dirCount++; }
      if (hasRefTok)  { hasRef  = true; }
      if (hasWorkTok) { hasWork = true; workCount++; }

      // Ensure syntax present
      if (!hasAs) pushError(i, 'LOAD requires "AS <TYPE>"');
      if (!(hasFileTok || hasDirTok || hasRefTok || hasWorkTok || hasLinesTok)) pushError(i, 'LOAD requires a type (FILE, DIR, REF, WORK, or LINES)');
      const hasName = /LOAD\s+[^\s]+/i.test(line);
      if (!hasName) pushError(i, 'LOAD requires a filename or path before AS');

      // Allow multiple FILE and DIR loads (common to stash into variables before batch work)
      // Only one WORK allowed per batch
      if (workCount > 1) pushError(i, 'Only one WORK is allowed per batch');
    }

    // CHECKPOINT syntax: allow ANY order of EVERY n, TIMEOUT n, MESSAGE "text"
    if (/^CHECKPOINT\b/i.test(line)) {
      const mEvery = line.match(/\bEVERY\s+(\d+)/i);
      if (mEvery && (isNaN(parseInt(mEvery[1], 10)) || parseInt(mEvery[1], 10) < 1)) pushError(i, 'CHECKPOINT EVERY requires a positive number');
      const mTimeout = line.match(/\bTIMEOUT\s+(\d+)/i);
      if (mTimeout && (isNaN(parseInt(mTimeout[1], 10)) || parseInt(mTimeout[1], 10) < 1)) pushError(i, 'CHECKPOINT TIMEOUT requires a positive number of seconds');
      if (/\bMESSAGE\b/i.test(line) && !/\bMESSAGE\s+("[\s\S]*?"|'[\s\S]*?')/i.test(line)) pushError(i, 'CHECKPOINT MESSAGE requires a quoted string');
      continue;
    }

    // VALIDATE syntax
    if (/^VALIDATE\b/i.test(line)) {
      const scopeOk = /\b(INPUT|OUTPUT)\b/i.test(line);
      if (!scopeOk) pushError(i, 'VALIDATE requires INPUT or OUTPUT');
      const hasMatch = /\bMATCH\b/i.test(line);
      const hasJson = /\bJSON_VALID\b/i.test(line);
      const hasSchema = /\bJSON_SCHEMA\b/i.test(line);
      const hasReq = /\bREQUIRED_KEYS\b/i.test(line);
      if (!(hasMatch || hasJson || hasSchema || hasReq)) pushError(i, 'VALIDATE requires MATCH "regex", JSON_VALID, JSON_SCHEMA, or REQUIRED_KEYS');
      if (hasMatch) {
        if (!/MATCH\s+("[\s\S]*?"|'[\s\S]*?')/i.test(line)) pushError(i, 'VALIDATE MATCH requires a quoted regex');
      }
      if (hasSchema) {
        if (!/JSON_SCHEMA\s+("[^"\r\n]+"|'[^'\r\n]+')/i.test(line)) pushError(i, 'VALIDATE JSON_SCHEMA requires a quoted path');
      }
      if (hasReq) {
        const ok = /REQUIRED_KEYS\s+("[^"]*"|'[^']*'|[A-Za-z_][A-Za-z0-9_]*(\s*,\s*[A-Za-z_][A-Za-z0-9_]*)*)/i.test(line);
        if (!ok) pushError(i, 'VALIDATE REQUIRED_KEYS expects "a,b" or identifiers a,b');
      }
      if (/\bON_FAIL\b/i.test(line)) {
        const af = line.match(/ON_FAIL\s+(SKIP|RETRY|HALT|ROUTE)\b/i);
        if (!af) pushError(i, 'VALIDATE ON_FAIL must be SKIP|RETRY|HALT|ROUTE');
        else if (af[1].toUpperCase() === 'ROUTE') {
          const hasPath = /ON_FAIL\s+ROUTE\s+\S+/i.test(line);
          if (!hasPath) pushError(i, 'VALIDATE ON_FAIL ROUTE requires a path');
        }
      }
    }

    // Variable usage check (undefined {var})
  const varRefs = line.match(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g) || [];
    for (const token of varRefs) {
      const raw = token.replace(/[{}]/g, '');
      const name = raw.toUpperCase();
      const isBuiltIn =
        /^COL\d+$/i.test(raw) ||
        name === 'LINE' ||
        name === 'LINENO' ||
        name === 'INPUT' ||
      name === 'INPUT_TEXT' ||
      name === 'FILENAME' ||
      name === 'STEPNUM' ||
      name === 'STEPTOT' ||
      name === 'LOOPCT' ||
      name === 'PREV' ||
      name === 'FULL';
      if (!definedVars.has(name) && !isBuiltIn) {
        pushError(i, `Undefined variable {${name}}`);
      }
    }
  }

  // Unmatched stacks at EOF
  while (batchStack.length) { const li = batchStack.pop(); pushError(li, 'Unclosed BATCH_START (missing END_BATCH)'); }
  while (ifStack.length)    { const li = ifStack.pop();    pushError(li, 'Unclosed IF (missing END_IF)'); }
  while (loopStack.length)  { const li = loopStack.pop();  pushError(li, 'Unclosed LOOP (missing END_LOOP)'); }
  while (routeStack.length) { const li = routeStack.pop(); pushError(li, 'Unclosed ROUTE_ON (missing END_ROUTE)'); }

  // Derive batchType heuristically (first batch encountered)
  // Rerun a cheap pass to compute first batch flags
  let bt = null;
  {
    let fHasRef=false,fHasFile=false,fHasDir=false,fHasWork=false;
    let started=false, finished=false;
    for (let i=0;i<lines.length;i++) {
      const line = lines[i].replace(/#.*/, '').trim();
      if (!line) continue;
      if (/^BATCH_START\b/i.test(line)) { started=true; continue; }
      if (started && /^END_BATCH\b/i.test(line)) { finished=true; break; }
      if (!started) continue;
      if (/\bREF\b/i.test(line)) fHasRef = true;
      if (/\bFILE\b/i.test(line)) fHasFile = true;
      if (/\bDIR\b/i.test(line)) fHasDir = true;
      if (/\bWORK\b/i.test(line)) fHasWork = true;
    }
    if (started) {
      if (fHasRef && (fHasFile || fHasDir) && fHasWork) bt = 'COMBO**';
      else if (fHasRef && fHasFile && fHasDir) bt = 'COMBO*';
      else if (fHasRef && (fHasFile || fHasDir)) bt = 'COMBO';
      else if (fHasRef) bt = 'PROMPT';
      else if ((fHasFile || fHasDir) && !fHasRef) bt = 'EXAMINE';
      else bt = null;
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    batchType: bt
  };
}


